import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import {
  WIDGETS,
  WIDGET_SLOTS,
  WEATHER_CREDIT,
  backyardTotalMeters,
  courseIsFull,
  courseLiveSlot,
  formatClock,
  formatDistance,
  formatTemp,
  largestSize,
  showsRaceList,
  showsSides,
  tickerKindIcon,
  weatherIcon,
  type AthleteSpotlight,
  type CornerSlot,
  type CoursePosition,
  type PipLayout,
  type ResultsStatus,
  type SportId,
  type SpotlightConfig,
  type SpotlightState,
  type TickerEntry,
  type TickerState,
  type WeatherState,
  type WidgetConfig,
  type WidgetId,
  type WidgetSize,
  type WidgetSlot,
  type WidgetState,
} from '@rabbitfuel/shared';
import { RaceList, Scoreboard, TickerClockLabel, usePagedList } from './Ticker.tsx';
import { COURSE_PAD, CourseMap, useCoursePositions } from './CourseMap.tsx';
import { courseShape } from '../lib/course.ts';
import { SponsorBug, type SponsorBugProps } from './Ads.tsx';
import { SocialIcon } from './SocialIcon.tsx';
import { overlayAccentVars } from '../lib/appearance.ts';
import type { LayerZoom } from '../lib/viewerLayers.ts';

/**
 * Einblendungen ueber dem Hauptbild.
 *
 * Das ist bewusst eine HTML-Ebene ueber dem <video> und *kein* ins Bild
 * gerechnetes Overlay: so bleibt die Schrift bei jeder Aufloesung scharf, es
 * kostet keine Bandbreite und keine CPU beim Sender, und die Regie sieht
 * Aenderungen sofort bei allen.
 *
 * In den RTMP-Stream fuer YouTube/Twitch kommen die Einblendungen trotzdem
 * mit: der Server oeffnet dafuer die Renderseite in einem unsichtbaren Chrome
 * und nimmt sie als Tab auf (`web/src/pages/Render.tsx`). Gerechnet wird also
 * nichts -- aufgenommen wird das fertig gesetzte HTML.
 *
 * Die Positionen sind fest (vier Ecken plus zwei Baender) und relativ zum Bild,
 * damit dieselbe Einblendung auf Handy und Beamer gleich sitzt. Wo genau
 * Platz frei ist, weiss der Player -- siehe `playerChrome` weiter unten.
 */
export function StreamOverlay({
  ticker,
  feed,
  widgets,
  sport,
  maxRows,
  sponsor,
  zoom,
  weather,
  course,
  results,
  spotlight,
}: {
  ticker: TickerState;
  feed: TickerEntry[];
  widgets: WidgetState;
  sport: SportId;
  /** Athleten-Pop-up -- das laufende und wo es steht. */
  spotlight?: SpotlightState | null;
  /** Vorhersage und Ansicht fuer die Wetter-Einblendung -- ohne sie bleibt die leer. */
  weather?: WeatherState | null;
  /** Die Strecke des Events (`courseOf`) -- ohne sie bleibt die Streckenkarte leer. */
  course?: [number, number][] | null;
  /** Zustand von RACE RESULT -- entscheidet, ob die Streckenkarte mit der „Runde ⌀" rechnet. */
  results?: ResultsStatus | null;
  /** Hoechstens so viele Zeilen je Seite der Strecken-Liste, egal welche Groesse. */
  maxRows?: number;
  /**
   * Sponsor-Logo. Es steht in derselben Ecke wie die Einblendungen und
   * stapelt sich mit ihnen -- so weicht es Live-Punkt, Knoepfen, kleinem
   * Fenster und Bauchbinde nach denselben Regeln aus.
   */
  sponsor?: SponsorBugProps | null;
  /**
   * Wie gross dieser Zuschauer die Einblendungen sehen will (siehe
   * viewerLayers.ts). Nur die Zuschauerseite gibt das mit -- Regie und
   * Renderseite zeigen die Groesse der Regie.
   */
  zoom?: LayerZoom;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  useUpperBandClearance(rootRef);
  useLowerBandZoom(rootRef, widgets, zoom);

  /*
   * Streckenkarte im Vollbild: sie ist dann das Bild. Die anderen
   * Einblendungen treten zurueck -- Loop, Feld und Uhr stehen in ihrer
   * Kopfzeile. Nur das Sponsor-Logo bleibt, es ist nicht abschaltbar.
   */
  const full = courseFullscreen(widgets, ticker, course);
  const spot = useSpotlightView(spotlight?.active ?? null);
  const bySlot = new Map<WidgetSlot, WidgetId[]>();
  for (const w of full ? [] : WIDGETS) {
    const config = widgets[w.id];
    if (!config?.on) continue;
    bySlot.set(config.slot, [...(bySlot.get(config.slot) ?? []), w.id]);
  }
  if (bySlot.size === 0 && !sponsor && !full && !spot) return null;

  return (
    <div className="stream-overlay" ref={rootRef}>
      {full && (
        <CourseFull
          ticker={ticker}
          course={course!}
          results={results}
          color={widgets.course.color}
          zoom={zoom?.course ?? 1}
        />
      )}
      {WIDGET_SLOTS.map(({ id: slot }) => {
        const ids = bySlot.get(slot) ?? [];
        const bug = sponsor?.config.slot === slot ? <SponsorBug key="sponsor" {...sponsor} /> : null;
        if (!ids.length && !bug) return null;
        // Das Logo sitzt der Bildecke am naechsten -- oben zuerst, unten zuletzt.
        const top = slot === 'topLeft' || slot === 'topRight';
        // Die Breitengrenze der Ecke steht in em der Grundschrift -- waechst
        // eine Einblendung darin beim Zuschauer, muss die Grenze mitwachsen.
        const slotZoom = Math.max(1, ...ids.map((id) => zoom?.[id] ?? 1));
        return (
          <div
            className={`so-slot so-slot--${slot}`}
            key={slot}
            style={slotZoom === 1 ? undefined : ({ '--so-slot-zoom': slotZoom } as CSSProperties)}
          >
            {top && bug}
            {ids.map((id) => (
              <Widget
                key={id}
                id={id}
                size={widgets[id].size}
                zoom={zoom?.[id] ?? 1}
                color={widgets[id].color}
                ticker={ticker}
                feed={feed}
                sport={sport}
                maxRows={maxRows}
                weather={weather}
                course={course}
                results={results}
              />
            ))}
            {!top && bug}
          </div>
        );
      })}
      {spot && spotlight && (
        <SpotlightCard
          key={spot.spot.id}
          spot={spot.spot}
          leaving={spot.leaving}
          config={spotlight.config}
          logoSlot={sponsor?.config.slot ?? null}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------- Athleten-Pop-up */

/** So lange bleibt das Pop-up nach dem Ende noch stehen -- die Zeit fuers Hinausgleiten. */
const SPOTLIGHT_EXIT_MS = 450;

/**
 * Das laufende Pop-up -- und nach dem Ende noch einen Moment das letzte, mit
 * `leaving`, damit es hinausgleiten kann statt zu verschwinden. Wann es endet,
 * sagt der Server (er nimmt es nach `SPOTLIGHT_MS` weg); so stimmt es auch
 * bei Uhren, die ein paar Sekunden falsch gehen.
 */
function useSpotlightView(active: AthleteSpotlight | null) {
  const [view, setView] = useState<{ spot: AthleteSpotlight; leaving: boolean } | null>(
    active ? { spot: active, leaving: false } : null,
  );
  useEffect(() => {
    if (active) {
      setView({ spot: active, leaving: false });
      return;
    }
    setView((current) => (current ? { ...current, leaving: true } : null));
    const timer = window.setTimeout(() => setView(null), SPOTLIGHT_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [active]);
  return view;
}

/** `Anna Winter` → `AW` -- statt Foto, wenn keins da ist. */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/**
 * Das Pop-up: Headshot rund, daneben Name, Startnummer, Team, ein Satz zur
 * Person und die Netzwerke. Es gleitet von der Seite seiner Ecke herein.
 *
 * Die Ecke stellt die Regie ein (`SpotlightConfig.slot`). Steht dort schon
 * das Logo, die zweite Kamera oder -- bei der Streckenkarte im Vollbild --
 * das Hauptbild, weicht es aus wie das Hauptbild dort (`CORNER_ALTERNATIVES`).
 * Wo was steht, liest es beim Erscheinen am Player ab (`playerChrome`,
 * `courseChrome`); bis dahin ist es unsichtbar, damit es nicht springt.
 * Einblendungen in derselben Ecke ueberdeckt es fuer die fuenf Sekunden.
 */
function SpotlightCard({
  spot,
  leaving,
  config,
  logoSlot,
}: {
  spot: AthleteSpotlight;
  leaving: boolean;
  config: SpotlightConfig;
  logoSlot: CornerSlot | null;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [slot, setSlot] = useState<CornerSlot | null>(null);
  useLayoutEffect(() => {
    const data = ref.current?.closest<HTMLElement>('.player')?.dataset ?? {};
    const blocked = new Set<string | null | undefined>([
      logoSlot,
      data.pip === 'on' ? data.pipSlot : null,
      data.courseFull === 'on' && data.courseLive === 'on' ? data.courseSlot : null,
    ]);
    setSlot(CORNER_ALTERNATIVES[config.slot].find((s) => !blocked.has(s)) ?? config.slot);
  }, [config.slot, logoSlot]);
  const tint = overlayAccentVars(config.color) as CSSProperties | undefined;
  return (
    <div
      ref={ref}
      className={`so-card so-spot${tint ? ' is-tinted' : ''}${leaving ? ' is-leaving' : ''}`}
      data-slot={slot ?? config.slot}
      data-size={config.size}
      style={{ ...tint, visibility: slot ? undefined : 'hidden' }}
      role="status"
    >
      <div className="so-spot__photo">
        {spot.photoUrl ? <img src={spot.photoUrl} alt="" /> : <span>{initials(spot.name)}</span>}
      </div>
      <div className="so-spot__body">
        <b className="so-spot__name">{spot.name}</b>
        <div className="so-spot__meta">
          <span className="so-spot__bib">{spot.bib}</span>
          {spot.team && <span className="so-spot__team">{spot.team}</span>}
        </div>
        {spot.info && <div className="so-spot__info">{spot.info}</div>}
        {spot.socials.length > 0 && (
          <ul className="so-spot__socials">
            {spot.socials.map((s) => (
              <li key={s.id}>
                <SocialIcon id={s.id} />
                <span>{s.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------- Platz im Bild melden */

/** Bedienelemente des Players, die schon Platz belegen. */
export interface PlayerChrome {
  /** Live-Punkt oben links. */
  badge?: boolean;
  /** Ton-/Vollbildknoepfe oben rechts. */
  controls?: boolean;
  /** Quellenschild unten links. */
  source?: boolean;
  /** Zweite Kamera -- als kleines Fenster oder im Splitscreen, je nach `PipLayout.mode`. */
  pip?: boolean;
  /**
   * Handy hochkant: Live-Punkt, Knoepfe und Quellenschild stehen unter dem
   * Bild, und alles im Bild rueckt enger an den Rand.
   */
  compact?: boolean;
  /**
   * Die Streckenkarte steht im Vollbild (`courseFullscreen`). Dann gibt es
   * keinen Splitscreen -- die zweite Kamera steht als kleines Fenster in ihrer
   * Ecke -- und keine Bauchbinde, die Platz braucht. Siehe `courseChrome`.
   */
  course?: boolean;
}

/**
 * Die `data-`Attribute fuers `.player`-Element.
 *
 * Sie stehen bewusst am Player und nicht an der Einblendungs-Ebene: aus
 * ihnen leitet das Stylesheet die Abstaende ab, und die brauchen nicht nur
 * die Einblendungen, sondern auch das kleine Fenster und das Quellenschild --
 * beides Geschwister der Ebene, keine Kinder. Eine Bauchbinde belegt das
 * untere Band, also ruecken die anderen beiden darueber.
 */
export function playerChrome(chrome: PlayerChrome, widgets: WidgetState, pip?: PipLayout) {
  // Bei mehreren Einblendungen in der Bauchbinde zaehlt die groesste.
  const lower = chrome.course
    ? null
    : largestSize(
        WIDGETS.filter((w) => widgets[w.id]?.on && widgets[w.id].slot === 'lower').map((w) => widgets[w.id].size),
      );
  const split = isSplit(pip, chrome.pip) && !chrome.course;

  return {
    'data-badge': chrome.badge ? 'on' : 'off',
    'data-controls': chrome.controls ? 'on' : 'off',
    'data-source': chrome.source ? 'on' : 'off',
    // Im Splitscreen steht die zweite Kamera nicht in einer Ecke -- sie haelt
    // dort also auch keinen Platz frei, den Einblendungen meiden muessten.
    'data-pip': chrome.pip && !split ? 'on' : 'off',
    /*
     * Splitscreen: Aufteilung, Seiten, Ausschnitt. Das Stylesheet setzt daraus
     * Hauptbild und zweite Kamera nebeneinander bzw. uebereinander -- dieselben
     * zwei Elemente wie sonst, sie stehen nur anders. So muss beim Umschalten
     * nichts neu verbinden.
     */
    'data-split': split ? pip!.split : 'off',
    'data-split-swap': split && pip!.swap ? 'on' : 'off',
    'data-split-fit': pip?.fit ?? 'cover',
    'data-compact': chrome.compact ? 'on' : 'off',
    // Ecke und Groesse des kleinen Fensters -- daraus leitet das Stylesheet
    // ab, welche Ecke fuer Einblendungen belegt ist.
    'data-pip-slot': pip?.slot ?? 'bottomRight',
    'data-pip-size': pip?.size ?? 'md',
    /*
     * Das untere Band schiebt immer, was darueber steht. Das obere nicht: es
     * steht mittig, und Live-Punkt wie Bedienknoepfe sitzen daneben, nicht
     * darunter. Sie wegzuschieben liesse die Uhr mitten im Bild schweben.
     * Ob die oberen Ecken trotzdem darunter muessen, ist keine Frage der
     * Einstellung, sondern des Platzes -- das misst `useUpperBandClearance`.
     */
    'data-lower': lower ?? 'off',
  };
}

/** Steht die zweite Kamera im Splitscreen neben dem Hauptbild? */
export const isSplit = (pip: PipLayout | undefined, second: unknown): boolean => Boolean(second) && pip?.mode === 'split';

/**
 * Steht die Streckenkarte gerade im Vollbild? Dafuer muss sie an sein, auf
 * Vollbild stehen und etwas zu zeigen haben (Backyard mit Strecke). Die
 * Zuschauerseite gibt die Einblendungen schon ohne das Ausgeblendete herein --
 * blendet jemand die Strecke fuer sich aus, ist das Hauptbild wieder gross.
 */
export function courseFullscreen(
  widgets: WidgetState,
  ticker: TickerState,
  course: [number, number][] | null | undefined,
): boolean {
  const config = widgets.course;
  return Boolean(config?.on && courseIsFull(config) && courseShape(course ?? null) && ticker.mode === 'backyard');
}

/**
 * Wohin das Hauptbild ausweicht: erst auf derselben Seite nach oben bzw.
 * unten, dann auf die andere Seite. Links steht die Tabelle der Laeufer --
 * wer rechts gewaehlt hat, soll ihr nicht ohne Not in die Quere kommen.
 */
const CORNER_ALTERNATIVES: Record<CornerSlot, CornerSlot[]> = {
  topLeft: ['topLeft', 'bottomLeft', 'topRight', 'bottomRight'],
  topRight: ['topRight', 'bottomRight', 'topLeft', 'bottomLeft'],
  bottomLeft: ['bottomLeft', 'topLeft', 'bottomRight', 'topRight'],
  bottomRight: ['bottomRight', 'topRight', 'bottomLeft', 'topLeft'],
};

/**
 * Die `data-`Attribute fuer die Streckenkarte im Vollbild. Das Hauptbild
 * rutscht dann klein in eine Ecke -- dasselbe Video-Element, nur anders
 * gesetzt, so wie in der Werbepause (siehe `adChrome`). Es muss also nichts
 * neu verbinden, und der Ton laeuft einfach weiter.
 *
 * Die Ecke waehlt die Regie (`courseLiveSlot`). Steht dort schon das kleine
 * Fenster der zweiten Kamera oder das Sponsor-Logo, weicht das Hauptbild in
 * die naechste freie Ecke aus. `live`: es gibt ein Bild -- sonst bliebe ein
 * leerer Rahmen in der Ecke stehen.
 *
 * `data-course-tl`/`-bl`: eine linke Ecke ist belegt (Hauptbild, zweite
 * Kamera oder Logo). Dort laesst die Tabelle der Laeufer Platz.
 */
export function courseChrome(
  full: boolean,
  config: WidgetConfig,
  { live, pipSlot, logoSlot }: { live: boolean; pipSlot: CornerSlot | null; logoSlot: CornerSlot | null },
) {
  const wanted = courseLiveSlot(config);
  const slot = CORNER_ALTERNATIVES[wanted].find((s) => s !== pipSlot && s !== logoSlot) ?? wanted;
  const taken = new Set<CornerSlot | null>([live ? slot : null, pipSlot, logoSlot]);
  return {
    'data-course-full': full ? 'on' : 'off',
    'data-course-live': full && live ? 'on' : 'off',
    'data-course-slot': slot,
    'data-course-size': config.size,
    'data-course-tl': taken.has('topLeft') ? 'on' : 'off',
    'data-course-bl': taken.has('bottomLeft') ? 'on' : 'off',
  };
}

/** Mindestabstand zwischen oberem Band und einer Ecke, bevor sie als Kollision gelten. */
const BAND_GAP_PX = 6;

/** Umriss mehrerer Elemente zusammen, oder null, wenn keins sichtbar ist. */
function spanOf(elements: Iterable<Element>): DOMRect | null {
  let span: DOMRect | null = null;
  for (const element of elements) {
    const r = element.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    span = span
      ? new DOMRect(
          Math.min(span.left, r.left),
          Math.min(span.top, r.top),
          Math.max(span.right, r.right) - Math.min(span.left, r.left),
          Math.max(span.bottom, r.bottom) - Math.min(span.top, r.top),
        )
      : r;
  }
  return span;
}

const releaseBand = (player: HTMLElement) => {
  delete player.dataset.upperStack;
  player.style.removeProperty('--so-upper-end');
};

/**
 * Setzt `data-upper-stack` am Player, sobald das obere Band und eine obere
 * Ecke (Einblendung oder kleines Fenster) sich in der Breite ins Gehege
 * kommen -- dann ruecken die Ecken unter das Band.
 *
 * Gemessen statt gerechnet, weil die Antwort am Inhalt haengt: die Backyard-
 * Anzeige ist bei „274,946 km" breiter als bei „20,1 km", die Uhr mit „von
 * 1:10:00" breiter als ohne, und jede Einblendung hat fuenf Groessen. Ein
 * fester Schwellwert war genau der Fehler: quer am Handy lagen Backyard und
 * Uhr uebereinander, weil das Band unter die Knoepfe und damit auf die Hoehe
 * der Uhr gerutscht war.
 *
 * Verglichen wird nur die Breite. Die aendert sich durchs Verschieben nicht --
 * die Messung kann also nicht hin- und herspringen.
 */
function useUpperBandClearance(rootRef: RefObject<HTMLDivElement | null>) {
  const playerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    const root = rootRef.current;
    const player = root?.closest<HTMLElement>('.player') ?? null;
    if (playerRef.current && playerRef.current !== player) releaseBand(playerRef.current);
    playerRef.current = player;
    if (!root || !player) return;

    // Groesse des Players, Inhalt der Einblendungen und das kleine Fenster --
    // alles, was die Antwort aendern kann, ohne dass React neu rendert
    // (Drehen, Vollbild, nachgeladene Schrift). `observe` ist idempotent.
    const pipSlot = player.dataset.pipSlot;
    const pip =
      player.dataset.pip === 'on' && (pipSlot === 'topLeft' || pipSlot === 'topRight')
        ? player.querySelector('.player__program > .pip')
        : null;
    for (const el of [root, ...root.children, ...(pip ? [pip] : [])]) observerRef.current?.observe(el);

    const band = spanOf(root.querySelectorAll(':scope > .so-slot--upper > *'));
    const corners = [
      spanOf(root.querySelectorAll(':scope > .so-slot--topLeft > *')),
      spanOf(root.querySelectorAll(':scope > .so-slot--topRight > *')),
      pip ? spanOf([pip]) : null,
    ];
    const clash =
      band &&
      corners.some((c) => c && c.left < band.right + BAND_GAP_PX && band.left < c.right + BAND_GAP_PX);
    if (!band || !clash) {
      releaseBand(player);
      return;
    }
    /*
     * `top` der Ecken bezieht sich auf das Programm, in dem sie stehen. Das
     * rueckt fuer Banner und L-Form zusammen (`transform: scale`) -- gemessen
     * wird dann im verkleinerten Bild, gesetzt aber in dessen eigenem Mass.
     * Daher der Faktor zwischen beidem.
     */
    const box = root.closest<HTMLElement>('.player__program') ?? player;
    const rect = box.getBoundingClientRect();
    const scale = rect.height > 0 && box.offsetHeight > 0 ? box.offsetHeight / rect.height : 1;
    const end = Math.ceil((band.bottom - rect.top) * scale - box.clientTop);
    player.dataset.upperStack = 'on';
    player.style.setProperty('--so-upper-end', `${end}px`);
  }, [rootRef]);

  useEffect(() => {
    const observer = new ResizeObserver(() => measure());
    observerRef.current = observer;
    measure();
    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (playerRef.current) releaseBand(playerRef.current);
      playerRef.current = null;
    };
  }, [measure]);

  // Nach jedem Rendern: Einblendungen und kleines Fenster koennen gekommen
  // oder gegangen sein. Vor dem Zeichnen, damit nichts einen Moment lang
  // uebereinander steht.
  useLayoutEffect(() => measure());
}

/** Die Faktoren der fuenf Groessen -- dieselben wie `.so-card--*` im Stylesheet. */
const SIZE_EM: Record<WidgetSize, number> = { xs: 0.65, sm: 0.8, md: 1, lg: 1.3, xl: 1.7 };

/**
 * Das untere Band haelt Platz frei fuer das, was darueber steht (Quellenschild,
 * kleines Fenster). Wie viel, leitet das Stylesheet aus der groessten
 * Einblendung darin ab (`data-lower`). Hat ein Zuschauer eine davon groesser
 * gestellt, muss der Platz mitwachsen -- `--so-lower-zoom` ist der Faktor
 * zwischen dem, was wirklich dort steht, und der Groesse aus `data-lower`.
 */
function useLowerBandZoom(rootRef: RefObject<HTMLDivElement | null>, widgets: WidgetState, zoom?: LayerZoom) {
  let factor = 1;
  if (zoom) {
    const lower = WIDGETS.filter((w) => widgets[w.id]?.on && widgets[w.id].slot === 'lower');
    const largest = largestSize(lower.map((w) => widgets[w.id].size));
    if (largest) {
      const needed = Math.max(...lower.map((w) => SIZE_EM[widgets[w.id].size] * (zoom[w.id] ?? 1)));
      factor = needed / SIZE_EM[largest];
    }
  }

  useLayoutEffect(() => {
    const player = rootRef.current?.closest<HTMLElement>('.player');
    if (!player) return;
    if (factor === 1) player.style.removeProperty('--so-lower-zoom');
    else player.style.setProperty('--so-lower-zoom', String(factor));
  });
}

/**
 * Ob ein eingeschalteter Baustein gerade ueberhaupt etwas zeigt. Eine leere
 * Notiz oder ein Stand beim Lauf bleiben unsichtbar -- dafuer soll beim
 * Zuschauer auch kein Schalter auftauchen, der nichts tut.
 */
export function widgetHasContent(
  id: WidgetId,
  ticker: TickerState,
  feed: TickerEntry[],
  sport: SportId,
  weather?: WeatherState | null,
  course?: [number, number][] | null,
): boolean {
  switch (id) {
    case 'scoreboard':
      return showsSides(ticker.mode, sport);
    case 'clock':
      return true;
    case 'lastEvent':
      return feed.length > 0;
    case 'list':
      return showsRaceList(ticker.mode) ? ticker.entries.length > 0 : feed.length > 0;
    case 'note':
      return Boolean(ticker.note);
    case 'backyard':
      return ticker.mode === 'backyard';
    case 'weather':
      return Boolean(weather?.report);
    case 'course':
      return ticker.mode === 'backyard' && Boolean(course && course.length >= 2);
    default:
      return false;
  }
}

function Widget({
  id,
  size,
  zoom,
  color,
  ticker,
  feed,
  sport,
  maxRows,
  weather,
  course,
  results,
}: {
  id: WidgetId;
  size: WidgetSize;
  /** Faktor des Zuschauers auf die Groesse der Regie, 1 = unveraendert. */
  zoom: number;
  /** Eigene Akzentfarbe der Einblendung, sonst Rot. */
  color?: string;
  ticker: TickerState;
  feed: TickerEntry[];
  sport: SportId;
  maxRows?: number;
  weather?: WeatherState | null;
  course?: [number, number][] | null;
  results?: ResultsStatus | null;
}) {
  if (!widgetHasContent(id, ticker, feed, sport, weather, course)) return null;
  // Eine eigene Farbe toent das ganze Kaestchen (`is-tinted`), nicht nur die
  // Akzente -- sonst saehe man sie bei Uhr und Liste kaum.
  const tint = overlayAccentVars(color) as CSSProperties | undefined;
  const card = `so-card so-card--${size}${tint ? ' is-tinted' : ''}`;
  const style = zoom === 1 ? tint : ({ ...tint, '--so-zoom': zoom } as CSSProperties);

  switch (id) {
    case 'scoreboard':
      return (
        <div className={`${card} so-card--board`} style={style}>
          <Scoreboard ticker={ticker} sport={sport} bug />
        </div>
      );

    case 'clock':
      // Im Bild nur die Zeit -- Abschnitt und Dauer stehen auf der Event-Seite.
      return (
        <div className={`${card} so-clock`} style={style}>
          <TickerClockLabel ticker={ticker} bare />
        </div>
      );

    case 'lastEvent':
      return <LastEventWidget feed={feed} card={card} style={style} />;

    case 'list':
      // Auf der Strecke ist die Teilnehmerliste die Nachricht, sonst sind es
      // die letzten Meldungen.
      if (showsRaceList(ticker.mode)) {
        return (
          <div className={`${card} so-list`} style={style}>
            <RaceList entries={ticker.entries} rows={maxRows ? Math.min(LIST_ROWS[size], maxRows) : LIST_ROWS[size]} />
          </div>
        );
      }
      return (
        <ol className={`${card} so-list so-list--feed`} style={style}>
          {feed.slice(0, 3).map((e) => (
            <li key={e.id}>
              {e.mark && <b>{e.mark}</b>} {e.text}
            </li>
          ))}
        </ol>
      );

    case 'note':
      return (
        <div className={`${card} so-note`} style={style}>
          {ticker.note}
        </div>
      );

    case 'backyard':
      return (
        <div className={`${card} so-backyard`} style={style}>
          <BackyardStat value={formatDistance(backyardTotalMeters(ticker.backyard))} label="gesamt" wide />
          <BackyardStat value={String(ticker.backyard.loops)} label={ticker.backyard.loops === 1 ? 'Loop' : 'Loops'} />
          <BackyardStat value={String(ticker.backyard.runners)} label="im Rennen" />
        </div>
      );

    case 'weather':
      return <WeatherWidget weather={weather!} size={size} card={card} style={style} />;

    case 'course':
      return (
        <CourseWidget
          ticker={ticker}
          course={course!}
          results={results}
          card={card}
          style={style}
          scale={SIZE_EM[size] * zoom}
        />
      );

    default:
      return null;
  }
}

/**
 * Die Karte passt in dieses Kaestchen, in em -- waechst also mit der Groesse
 * der Einblendung. Zusaetzlich ist sie hoechstens `--course-cap` Prozent der
 * Bildbreite hoch (Stylesheet, `.so-course`): die Schrift der Einblendungen
 * ist erst ab 625 px Bildbreite gedeckelt, und darunter wuchse die Karte sonst
 * ohne Mass. Auf kleinen Bildern ist die Grenze hoeher -- dort steht nur die
 * Karte, ohne Kopf- und Fusszeile.
 */
const COURSE_BOX_EM = { w: 12, h: 9 };

/**
 * Die Runde mit den Startnummern darauf, so breit wie die Karte: oben eine
 * schmale Kopfzeile, unten klein „geschätzt" -- die Punkte kommen aus der
 * Rundenzeit, nicht aus einem GPS-Signal -- und wer schon zurueck am Start ist.
 */
function CourseWidget({
  ticker,
  course,
  results,
  card,
  style,
  scale,
}: {
  ticker: TickerState;
  course: [number, number][];
  results?: ResultsStatus | null;
  card: string;
  style?: CSSProperties;
  /** Groesse der Einblendung mal Zoom des Zuschauers -- fuer die Grenze in cqw, die am em vorbeigeht. */
  scale: number;
}) {
  const shape = useMemo(() => courseShape(course), [course]);
  const positions = useCoursePositions(ticker, results);
  if (!shape) return null;
  const ratio = (shape.width + COURSE_PAD * 2) / (shape.height + COURSE_PAD * 2);
  const heightEm = Math.min(COURSE_BOX_EM.h, COURSE_BOX_EM.w / ratio);
  const size = {
    '--course-h': heightEm.toFixed(2),
    // Eine quer geformte Strecke ist flacher -- die Grenze in cqw schrumpft mit.
    '--course-fill': (heightEm / COURSE_BOX_EM.h).toFixed(3),
    '--course-ratio': ratio.toFixed(4),
    // L und XL duerfen an der Grenze nur wenig mehr Platz nehmen als M --
    // sonst schoebe sich die Karte am Handy ueber Logo und kleines Fenster.
    '--course-scale': Math.min(scale, 1.2).toFixed(2),
  } as CSSProperties;
  const back = positions.filter((p) => p.back);
  return (
    <div className={`${card} so-course`} style={style}>
      <div className="so-course__head">
        <b>Strecke</b>
        <span>Loop {ticker.backyard.loops + 1}</span>
      </div>
      <CourseMap shape={shape} positions={positions} style={size} />
      <div className="so-course__foot">
        geschätzt
        {back.length > 0 && ` · zurück: ${back.map((p) => p.bib || p.name).join(' · ')}`}
      </div>
    </div>
  );
}

/** Unter der Karte: woher die Punkte kommen. RACE RESULT gewinnt, sobald es fuer einen gilt. */
function courseSourceNote(positions: CoursePosition[]): string {
  if (positions.some((p) => p.source === 'raceresult')) return 'geschätzt aus der Runde ⌀ von RACE RESULT';
  if (positions.some((p) => p.source === 'measured')) return 'geschätzt aus den letzten Rundenzeiten';
  return 'geschätzt aus der Standard-Rundenzeit';
}

/** Wer schon zurueck ist zuerst, dann wer am weitesten auf der Runde ist. */
const byCourseOrder = (a: CoursePosition, b: CoursePosition) =>
  Number(b.back) - Number(a.back) ||
  b.fraction - a.fraction ||
  a.bib.localeCompare(b.bib, 'de', { numeric: true });

/** So lange steht eine Seite der Tabelle -- Namen brauchen laenger als Zahlen. */
const COURSE_TABLE_PAGE_MS = 6000;

/**
 * Die Laeufer neben der Karte: Startnummer, Name und die Rundenzeit, mit der
 * ihr Punkt laeuft (`loopPrognosis`). Damit ist auf der Karte zu erkennen, wer
 * wer ist. Passt nicht alles in die Hoehe, blaettert die Tabelle -- wie viele
 * Zeilen passen, wird gemessen, weil Hoehe und Schrift am Bild haengen.
 */
function CourseTable({ positions }: { positions: CoursePosition[] }) {
  const rows = useMemo(() => [...positions].sort(byCourseOrder), [positions]);
  const listRef = useRef<HTMLOListElement | null>(null);
  const [fit, setFit] = useState(rows.length || 1);
  const any = rows.length > 0;
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const row = list.firstElementChild as HTMLElement | null;
      if (!row?.offsetHeight) return;
      setFit(Math.max(1, Math.floor(list.clientHeight / row.offsetHeight)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [any]);
  const start = usePagedList(rows.length, fit, COURSE_TABLE_PAGE_MS);
  const pages = Math.ceil(rows.length / fit);
  const page = Math.floor(start / fit);
  if (!rows.length) return null;
  return (
    <div className="so-course-table">
      <div className="so-course-table__head" aria-hidden>
        <span>Nr.</span>
        <span>Name</span>
        <span>Prognose</span>
      </div>
      <ol className="so-course-table__rows" ref={listRef}>
        {rows.slice(start, start + fit).map((p) => (
          <li key={p.entryId} className={p.back ? 'is-back' : p.measured ? undefined : 'is-guess'}>
            <span className="so-course-table__bib">{p.bib || '—'}</span>
            <span className="so-course-table__name">{p.name}</span>
            <span className="so-course-table__time">{p.back ? 'zurück' : formatClock(p.loopSeconds)}</span>
          </li>
        ))}
      </ol>
      {pages > 1 && (
        <div className="so-course-table__pages" aria-hidden>
          {Array.from({ length: pages }, (_, i) => (
            <span key={i} className={i === page ? 'is-on' : ''} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Die Streckenkarte im Vollbild: die Runde so gross, wie das Bild es
 * hergibt, links daneben die Laeufer als Tabelle, darueber Loop, Feld und
 * Uhr, darunter, woher die Schaetzung kommt und wer schon zurueck ist. Das
 * Hauptbild steht derweil klein in einer Ecke (`courseChrome`); rechts der
 * Karte bleibt dafuer genug Rand, dass es keine Startnummer verdeckt.
 *
 * Die Schrift waechst mit der Bildbreite (Stylesheet, `.so-course-full`),
 * nicht mit der Groesse der Einblendung -- die gilt im Vollbild dem Hauptbild
 * in der Ecke.
 *
 * Hochkant (Handy aufrecht im Vollbild) waeren die Raender links und rechts
 * breiter als die Karte -- dann bleibt der Platz fuer die Ecken oben und unten
 * frei (`is-tall`). Gemessen statt per Media-Query, weil nur das Bild zaehlt,
 * nicht das Fenster.
 */
function CourseFull({
  ticker,
  course,
  results,
  color,
  zoom,
}: {
  ticker: TickerState;
  course: [number, number][];
  results?: ResultsStatus | null;
  color?: string;
  zoom: number;
}) {
  const shape = useMemo(() => courseShape(course), [course]);
  const positions = useCoursePositions(ticker, results);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [tall, setTall] = useState(false);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => setTall(root.clientHeight > root.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [shape]);
  if (!shape) return null;
  const tint = overlayAccentVars(color) as CSSProperties | undefined;
  const style = {
    ...tint,
    '--course-ratio': ((shape.width + COURSE_PAD * 2) / (shape.height + COURSE_PAD * 2)).toFixed(4),
    ...(zoom === 1 ? {} : { '--so-zoom': zoom }),
  } as CSSProperties;
  const back = positions.filter((p) => p.back);
  const runners = ticker.backyard.runners;
  return (
    <div ref={rootRef} className={`so-course-full${tint ? ' is-tinted' : ''}${tall ? ' is-tall' : ''}`} style={style}>
      <div className="so-course-full__head">
        <b>Strecke</b>
        <span>Loop {ticker.backyard.loops + 1}</span>
        {runners > 0 && <span>{runners} im Rennen</span>}
        <span className="so-course-full__clock">
          <TickerClockLabel ticker={ticker} bare />
        </span>
      </div>
      <div className="so-course-full__body">
        <CourseTable positions={positions} />
        <div className="so-course-full__map">
          <CourseMap shape={shape} positions={positions} />
        </div>
      </div>
      <div className="so-course-full__foot">
        {courseSourceNote(positions)}
        {back.length > 0 && ` · zurück: ${back.map((p) => p.bib || p.name).join(' · ')}`}
      </div>
    </div>
  );
}

/**
 * Hoechstens so viele Stunden. Wie viele davon wirklich zu sehen sind,
 * entscheidet die Breite der Ecke (`.so-weather__hours`): was nicht mehr
 * passt, rutscht in eine unsichtbare zweite Zeile, statt halb abgeschnitten
 * im Bild zu stehen.
 */
const WEATHER_HOURS = 8;

const mm = (value: number) => `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)} mm`;

/**
 * Wetter am Event-Ort, in der Ansicht, die die Regie gewaehlt hat. Die Quelle
 * steht klein dabei -- CC BY 4.0 verlangt sie dort, wo die Daten zu sehen sind.
 */
function WeatherWidget({
  weather,
  size,
  card,
  style,
}: {
  weather: WeatherState;
  size: WidgetSize;
  card: string;
  style?: CSSProperties;
}) {
  const report = weather.report!;
  const view = weather.config.view;

  if (view === 'now') {
    const { icon, text } = weatherIcon(report.now.symbol);
    return (
      <div className={`${card} so-weather so-weather--now`} style={style}>
        <span className="so-weather__icon" aria-hidden>
          {icon}
        </span>
        <b className="so-weather__temp">{formatTemp(report.now.temp)}</b>
        <div className="so-weather__meta">
          <span className="so-weather__place">{report.place}</span>
          <span>
            {[text, `Wind ${report.now.windKmh} km/h`, report.now.precipMm > 0 ? mm(report.now.precipMm) : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <small className="so-weather__credit">{WEATHER_CREDIT}</small>
        </div>
      </div>
    );
  }

  if (view === 'days') {
    return (
      <div className={`${card} so-weather so-weather--days`} style={style}>
        <div className="so-weather__head">
          <span className="so-weather__place">Wetter · {report.place}</span>
        </div>
        {report.days.slice(0, size === 'xl' || size === 'lg' ? 2 : 3).map((day) => (
          <div className="so-weather__day" key={day.label}>
            <span className="so-weather__label">{day.label}</span>
            <span aria-hidden>{weatherIcon(day.symbol).icon}</span>
            <b>
              {formatTemp(day.tempMax)} <span className="so-weather__low">/ {formatTemp(day.tempMin)}</span>
            </b>
            <span className="so-weather__rain">{day.precipMm > 0 ? mm(day.precipMm) : '–'}</span>
          </div>
        ))}
        <small className="so-weather__credit">{WEATHER_CREDIT}</small>
      </div>
    );
  }

  const hours = report.hours.slice(0, WEATHER_HOURS);
  // Ist alles trocken, stuende unter jeder Stunde nur ein Strich -- dann lieber gar nichts.
  const wet = hours.some((hour) => hour.precipMm > 0);
  return (
    <div className={`${card} so-weather so-weather--hours`} style={style}>
      <div className="so-weather__head">
        <span className="so-weather__place">Wetter · {report.place}</span>
      </div>
      <div className="so-weather__hours">
        {hours.map((hour) => (
          <div className="so-weather__hour" key={hour.at}>
            <span className="so-weather__label">{hour.label}</span>
            <span className="so-weather__icon" aria-hidden>
              {weatherIcon(hour.symbol).icon}
            </span>
            <b>{formatTemp(hour.temp)}</b>
            {wet && <span className="so-weather__rain">{hour.precipMm > 0 ? mm(hour.precipMm) : '–'}</span>}
          </div>
        ))}
      </div>
      <small className="so-weather__credit">{WEATHER_CREDIT}</small>
    </div>
  );
}

/** Wie viele Zeilen die Strecken-Liste je Groesse zeigt, bevor sie umblaettert. */
const LIST_ROWS: Record<WidgetSize, number> = { xs: 2, sm: 3, md: 5, lg: 7, xl: 9 };

/**
 * Eine Zahl der Backyard-Anzeige. Der Wert steht gross, das Wort klein
 * darunter -- so liest sich „20,1 km / gesamt" auch aus zehn Metern
 * Entfernung noch als Einheit.
 */
function BackyardStat({ value, label, wide = false }: { value: string; label: string; wide?: boolean }) {
  return (
    <div className={`so-backyard__stat${wide ? ' so-backyard__stat--wide' : ''}`}>
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

/** Wie viele Meldungen im Laufband hintereinander stehen -- mehr wird schnell unleserlich. */
const MARQUEE_ENTRIES = 8;
/** Lesegeschwindigkeit: grosszuegige 9 Zeichen pro Sekunde. */
const MARQUEE_CHARS_PER_SEC = 9;
const MARQUEE_MIN_SECONDS = 18;

/**
 * Bauchbinde fuer "letztes Ereignis". Bei genau einer Meldung reicht eine
 * stehende Zeile; ab zwei Meldungen laeuft ein Laufband durch alle -- so wie
 * bei einer Nachrichtenzeile im Fernsehen. Die Dauer richtet sich nach der
 * Textmenge, damit auch bei vielen Meldungen jede lesbar bleibt.
 */
function LastEventWidget({ feed, card, style }: { feed: TickerEntry[]; card: string; style?: CSSProperties }) {
  const items = feed.slice(0, MARQUEE_ENTRIES);
  if (!items.length) return null;

  if (items.length === 1) {
    const only = items[0];
    return (
      <div className={`${card} so-bar`} style={style}>
        {only.mark && <span className="so-bar__mark">{only.mark}</span>}
        <span aria-hidden>{tickerKindIcon(only.kind)}</span>
        <span className="so-bar__text">{only.text}</span>
        {only.score && <span className="so-bar__score">{only.score}</span>}
      </div>
    );
  }

  const totalChars = items.reduce((n, e) => n + e.mark.length + e.text.length + (e.score?.length ?? 0), 0);
  const seconds = Math.max(MARQUEE_MIN_SECONDS, Math.round(totalChars / MARQUEE_CHARS_PER_SEC));

  return (
    <div className={`${card} so-marquee`} style={{ ...style, ['--so-marquee-duration' as string]: `${seconds}s` }}>
      {/* Der Inhalt liegt zweifach hintereinander -- die Animation verschiebt
          um genau die halbe Gesamtbreite und springt dann unsichtbar zurueck. */}
      <div className="so-marquee__track">
        {[0, 1].map((copy) => (
          <div className="so-marquee__set" key={copy} aria-hidden={copy === 1 || undefined}>
            {items.map((e) => (
              <span className="so-marquee__item" key={`${copy}-${e.id}`}>
                {e.mark && <b>{e.mark}</b>}
                <span aria-hidden>{tickerKindIcon(e.kind)}</span>
                <span>{e.text}</span>
                {e.score && <em>{e.score}</em>}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
